import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeroBookBadge from './HeroBookBadge';
import './hero-book-badge.css';

export default function HeroSection() {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (textRef.current) {
        textRef.current.classList.add('opacity-100');
        textRef.current.classList.remove('opacity-0', 'translate-y-8');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <section className="tjba-hero relative h-screen min-h-[700px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/generated/hero-butler-2026.jpg"
            alt={t('home.hero.imgAlt')}
            title={t('home.hero.imgTitle')}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/60 via-primary-900/45 to-primary-900/65"></div>

        {/* Gold accent line overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-accent-500/5 via-transparent to-accent-500/5"></div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 w-full">
            <div
              ref={textRef}
              className="max-w-2xl opacity-0 translate-y-8 transition-all duration-1000 ease-out"
            >
              {/* Brand Emblem */}
              <img
                src="/images/brand/logo-tjba-2026.png"
                alt="日本執事アカデミー TJBA"
                className="hidden md:block h-48 md:h-64 w-auto mb-8 object-contain drop-shadow-[0_2px_16px_rgba(0,0,0,0.55)]"
              />
              {/* Association Badge */}
              {String(t('home.hero.badge')).split(/\n{2,}/).map((__s, __i) => (
                <p key={__i} className="text-accent-400/80 text-[14px] tracking-[0.2em] mb-8 font-label">{__s}</p>
              ))}

              {/* Main Copy */}
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 leading-tight tracking-[0.04em] mb-6">
                {t('home.hero.title')}<span className="text-accent-300">{t('home.hero.titleAccent')}</span>
              </h1>

              {/* English Subtitle */}
              {String(t('home.hero.subtitle')).split(/\n{2,}/).map((__s, __i) => (
                <p key={__i} className="section-title-en text-accent-400/70 text-lg md:text-xl tracking-[0.15em] mb-8">{__s}</p>
              ))}

              {/* Description */}
              {String(t('home.hero.description')).split(/\n{2,}/).map((__s, __i) => (
                <p key={__i} className="text-foreground-300/80 text-[17px] leading-relaxed tracking-[0.04em] mb-10 max-w-xl">{__s}</p>
              ))}

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/personal"
                  className="btn-glow px-8 py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium text-center transition-all duration-300 whitespace-nowrap"
                >
                  {t('home.hero.ctaPersonal')}
                </Link>
                <Link
                  to="/corporate"
                  className="btn-glow px-8 py-3.5 border border-accent-500/50 text-accent-300 hover:text-accent-200 hover:border-accent-400 hover:bg-accent-500/8 text-[15px] tracking-[0.1em] text-center transition-all duration-300 whitespace-nowrap"
                >
                  {t('home.hero.ctaCorporate')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce">
          <span className="text-[12px] text-foreground-400 tracking-[0.15em]">SCROLL</span>
          <div className="w-[1px] h-8 bg-gradient-to-b from-accent-500/40 to-transparent"></div>
        </div>
      </section>
      <HeroBookBadge />
    </>
  );
}
